import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../store/useAuthStore';

// Brouillons de messages non envoyés, persistés par canal ET par collaborateur.
//
// Deux problèmes traités :
//  1. Fermer / réduire le sidecar démonte MessageComposer → le texte tapé était perdu.
//  2. Changer de canal réutilisait la même instance de MessageComposer sans reset :
//     le brouillon d'un canal restait affiché dans un autre (risque d'envoi au mauvais
//     destinataire). Un brouillon indexé par canal corrige les deux.
//
// SECRET PROFESSIONNEL — c'est le seul endroit de l'app qui persiste du contenu métier
// en clair (les autres usages de localStorage ne stockent que des ids et des préférences).
// Un brouillon peut contenir un nom de client ou une stratégie procédurale. Deux garde-fous :
//  • la clé est scopée par collaborateur → sur un poste partagé, un avocat ne voit jamais
//    le brouillon d'un confrère ;
//  • clearAllDrafts() est appelé à la déconnexion (voir AuthProvider.useSignOut) → rien ne
//    survit à la fin de session.
// Le chiffrement au repos n'apporterait rien de plus ici : la clé devrait vivre dans le
// même navigateur. La vraie protection reste le chiffrement disque du poste.

const PREFIX = 'cime.chat.draft.';
/** Un brouillon anormalement long est probablement un collage accidentel : on ne le persiste pas. */
const MAX_DRAFT_CHARS = 20_000;

/** Clé scopée par collaborateur : un brouillon n'est jamais visible par un autre utilisateur du poste. */
export function draftKey(collaboratorId: string, channelId: string): string {
  return `${PREFIX}${collaboratorId}.${channelId}`;
}

/** Lit le brouillon. Renvoie '' si absent ou si le stockage est indisponible. */
export function readDraft(collaboratorId: string, channelId: string): string {
  if (!collaboratorId || !channelId) return '';
  try {
    return localStorage.getItem(draftKey(collaboratorId, channelId)) ?? '';
  } catch {
    // localStorage peut lever (mode privé, quota, storage désactivé) — le brouillon
    // est un confort, jamais un bloquant.
    return '';
  }
}

/** Écrit le brouillon. Un brouillon vide supprime l'entrée plutôt que d'écrire ''. */
export function writeDraft(collaboratorId: string, channelId: string, value: string): void {
  if (!collaboratorId || !channelId) return;
  try {
    if (!value.trim() || value.length > MAX_DRAFT_CHARS) {
      localStorage.removeItem(draftKey(collaboratorId, channelId));
      return;
    }
    localStorage.setItem(draftKey(collaboratorId, channelId), value);
  } catch {
    // idem : on échoue en silence.
  }
}

/**
 * Purge TOUS les brouillons du poste, quel que soit le collaborateur.
 * Appelé à la déconnexion : aucun contenu de message ne survit à la fin de session.
 */
export function clearAllDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // idem.
  }
}

/**
 * État du composer pour un canal, persisté dans localStorage et scopé au collaborateur connecté.
 * Même signature que useState<string> — les appelants n'ont rien à changer.
 */
export function useChannelDraft(channelId: string): [string, (next: string) => void] {
  const collaboratorId = useAuthStore((s) => s.collaborator?.id) ?? '';
  const [value, setValue] = useState(() => readDraft(collaboratorId, channelId));

  // Changement de canal (ou d'utilisateur) : recharger le brouillon de la nouvelle cible.
  // Sans ça, React réutilise l'instance et le texte du canal précédent reste affiché.
  useEffect(() => {
    setValue(readDraft(collaboratorId, channelId));
  }, [collaboratorId, channelId]);

  const set = useCallback(
    (next: string) => {
      setValue(next);
      writeDraft(collaboratorId, channelId, next);
    },
    [collaboratorId, channelId],
  );

  return [value, set];
}
